// Project settings (`projects.options`) shared by server and web — the audio
// ones (spec: docs/project/audio-spec.md) and `interactive`, the
// non-interactive mode of docs/project/guest-flow-spec.md. A leaf like the
// rest of protocol/: constants, types and the validators `update_project`
// applies. The voices are ElevenLabs library/premade voices, all spoken by
// eleven_v3; the first is the default. `audio/` reads them for generation, the
// audio sheet for its picker. (Was protocol/audio.ts until 2026-09-22.)

export const VOICES = [
  { id: "mlvXFS1MP5qndOFkWz1M", name: "Edmund", blurb: "warm, British, unhurried" },
  { id: "Xb7hH8MSUJpSbSDYk0k2", name: "Alice", blurb: "clear, confident, British" },
  { id: "hpp4J3VqNfWAUOO0d1Us", name: "Bella", blurb: "soft, warm, American" },
  { id: "nPczCjzI2devNBz1zQrb", name: "Brian", blurb: "deep, steady, American" },
  { id: "JBFqnCBsd6RMkjVDRZzb", name: "George", blurb: "warm British storyteller" },
  { id: "FGY2WhTYpPnrIDTdsKH5", name: "Laura", blurb: "bright, upbeat, American" },
] as const;

export type VoiceId = (typeof VOICES)[number]["id"];
/** A voice, or no voiceover for this project. */
export type VoiceSetting = VoiceId | "none";

/** The pace notches: each clip is time-stretched by this factor after synthesis
 * (eleven_v3 ignores its own speed setting — see the spec's verified facts). */
export const PACES = [0.8, 0.9, 1, 1.1, 1.2] as const;
export type Pace = (typeof PACES)[number];

/** The music bed's level in dB (0 = the track as generated), what the sheet's slider spans. */
export const MUSIC_DB = { min: -20, max: 0, step: 2 } as const;

/** The bed's gain while a speech clip plays, relative to its level (the mixer's duck). */
export const DUCK_GAIN = 0.3;
export const DUCK_DB = Math.round(20 * Math.log10(DUCK_GAIN) * 10) / 10;

export const dbToGain = (db: number): number => 10 ** (db / 20);

/** A word as it appears in narration text and the text sent to the voice in its place
 * (a respelling like "par-KAY", or IPA between slashes). */
export interface Pronunciation {
  word: string;
  say: string;
}

/** `projects.options` as clients see it — the stored jsonb may hold a subset; toWire fills the rest. */
export interface ProjectOptions {
  voice: VoiceSetting;
  /** The music direction; the length rides in the text (no API length parameter). */
  musicPrompt: string;
  pace: Pace;
  musicDb: number;
  pronunciations: Pronunciation[];
  /** False = no user is present for this project's runs: the preamble says so and an
   * explorer ask closes at once instead of waiting for an answer. */
  interactive: boolean;
}

export const DEFAULT_OPTIONS: ProjectOptions = {
  voice: VOICES[0].id,
  musicPrompt: "30s instrumental for a product commercial",
  pace: 1,
  musicDb: -4,
  pronunciations: [],
  interactive: true,
};

export function isVoiceSetting(value: unknown): value is VoiceSetting {
  return value === "none" || VOICES.some((v) => v.id === value);
}

export function isPace(value: unknown): value is Pace {
  return PACES.some((p) => p === value);
}

export function isMusicDb(value: unknown): value is number {
  return (
    typeof value === "number" &&
    value >= MUSIC_DB.min &&
    value <= MUSIC_DB.max &&
    (value - MUSIC_DB.min) % MUSIC_DB.step === 0
  );
}

export function isPronunciations(value: unknown): value is Pronunciation[] {
  return (
    Array.isArray(value) &&
    value.every(
      (p) =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as Pronunciation).word === "string" &&
        (p as Pronunciation).word.trim() !== "" &&
        typeof (p as Pronunciation).say === "string" &&
        (p as Pronunciation).say.trim() !== "",
    )
  );
}

/** The picker label for a setting; "none" for no voiceover. */
export function voiceName(voice: VoiceSetting): string {
  return VOICES.find((v) => v.id === voice)?.name ?? "none";
}
